import React from 'react';
import { PLAYER_UI, RADII, FONTS } from '../../../theme';
import { KeyBinding } from '../../../../../shared/CharacterTypes';
import { ACTION_METADATA } from '../keybindingGroups';

interface KeyboardVisualizerProps {
  bindings: readonly KeyBinding[];
  highlightedAction: string | null;
  onSelectAction?: (action: string) => void;
  /** Bars the character currently has, shown in the legend. */
  activeBarIds?: readonly number[];
  /** When a specific Action Bar is selected in the rail, prioritize its bindings on shared keycaps. */
  focusedBarId?: number | null;
  /** Compact sizing for side-by-side desktop layout inside KeyBindingsWindow. */
  compact?: boolean;
}

interface KeyDef {
  key: string;
  label: string;
  width?: number; // relative flex or fixed px
}

const KEY_ROWS: KeyDef[][] = [
  // Row 0
  [
    { key: 'ESCAPE', label: 'Esc', width: 42 },
    { key: '1', label: '1' },
    { key: '2', label: '2' },
    { key: '3', label: '3' },
    { key: '4', label: '4' },
    { key: '5', label: '5' },
    { key: '6', label: '6' },
    { key: '7', label: '7' },
    { key: '8', label: '8' },
    { key: '9', label: '9' },
    { key: '0', label: '0' },
  ],
  // Row 1
  [
    { key: 'TAB', label: 'Tab', width: 44 },
    { key: 'Q', label: 'Q' },
    { key: 'W', label: 'W' },
    { key: 'E', label: 'E' },
    { key: 'R', label: 'R' },
    { key: 'T', label: 'T' },
    { key: 'Y', label: 'Y' },
    { key: 'U', label: 'U' },
    { key: 'I', label: 'I' },
    { key: 'O', label: 'O' },
    { key: 'P', label: 'P' },
  ],
  // Row 2
  [
    { key: 'A', label: 'A' },
    { key: 'S', label: 'S' },
    { key: 'D', label: 'D' },
    { key: 'F', label: 'F' },
    { key: 'G', label: 'G' },
    { key: 'H', label: 'H' },
    { key: 'J', label: 'J' },
    { key: 'K', label: 'K' },
    { key: 'L', label: 'L' },
    { key: 'ENTER', label: 'Enter', width: 50 },
  ],
  // Row 3
  [
    { key: 'SHIFT', label: 'Shift', width: 52 },
    { key: 'Z', label: 'Z' },
    { key: 'X', label: 'X' },
    { key: 'C', label: 'C' },
    { key: 'V', label: 'V' },
    { key: 'B', label: 'B' },
    { key: 'N', label: 'N' },
    { key: 'M', label: 'M' },
    { key: 'UP', label: '▲', width: 34 },
  ],
  // Row 4
  [
    { key: 'CTRL', label: 'Ctrl', width: 44 },
    { key: 'ALT', label: 'Alt', width: 40 },
    { key: 'SPACE', label: 'SPACE BAR', width: 150 },
    { key: 'LEFT', label: '◀', width: 34 },
    { key: 'DOWN', label: '▼', width: 34 },
    { key: 'RIGHT', label: '▶', width: 34 },
  ],
];

export const BAR_KEY_COLORS: Record<number, { bg: string; border: string; text: string }> = {
  1: { bg: 'rgba(230, 126, 34, 0.24)', border: '#c25e10', text: '#6e3002' },
  2: { bg: 'rgba(52, 152, 219, 0.22)', border: '#236a9c', text: '#143e5e' },
  3: { bg: 'rgba(155, 89, 182, 0.22)', border: '#783991', text: '#4a1f5c' },
  4: { bg: 'rgba(46, 204, 113, 0.22)', border: '#227844', text: '#134a28' },
  5: { bg: 'rgba(231, 76, 60, 0.22)', border: '#ad2e21', text: '#6e170e' },
  6: { bg: 'rgba(26, 188, 156, 0.22)', border: '#147d68', text: '#0b4a3d' },
};

const CATEGORY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  movement: { bg: 'rgba(47, 138, 76, 0.22)', border: '#2b7340', text: '#174523' },
  combat: { bg: 'rgba(210, 110, 24, 0.24)', border: '#b35810', text: '#6b3004' },
  ui: { bg: 'rgba(44, 110, 170, 0.20)', border: '#296094', text: '#153859' },
  targeting: { bg: 'rgba(142, 68, 173, 0.20)', border: '#783894', text: '#4a1d5e' },
  misc: { bg: 'rgba(212, 160, 23, 0.26)', border: '#9c710f', text: '#593e03' },
  event: { bg: 'rgba(22, 148, 124, 0.22)', border: '#147562', text: '#0a4539' },
};

export const KeyboardVisualizer: React.FC<KeyboardVisualizerProps> = ({
  bindings,
  highlightedAction,
  onSelectAction,
  activeBarIds = [1],
  focusedBarId = null,
  compact = false,
}) => {
  // Map normalized key string -> binding list (plain/unmodified keys first)
  const keyToBindingsMap = React.useMemo(() => {
    const map = new Map<string, KeyBinding[]>();
    for (const b of bindings) {
      const k = b.key.toUpperCase();
      const list = map.get(k) ?? [];
      list.push(b);
      map.set(k, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (a.modifier ? 1 : 0) - (b.modifier ? 1 : 0));
    }
    return map;
  }, [bindings]);

  return (
    <div style={boardContainerStyle} data-testid="keyboard-visualizer">
      {/* Legend */}
      <div style={legendRowStyle}>
        <span style={legendTitleStyle}>Visual Keyboard Map:</span>
        {activeBarIds.map((barId) => {
          const colors = BAR_KEY_COLORS[barId] ?? BAR_KEY_COLORS[1];
          return (
            <div key={`bar-${barId}`} style={legendItemStyle} data-testid={`keyboard-legend-bar-${barId}`}>
              <span style={{ ...legendDotStyle, background: colors.border }} />
              <span>Bar {barId}</span>
            </div>
          );
        })}
        {Object.entries(CATEGORY_COLORS)
          .filter(([cat]) => cat !== 'combat')
          .map(([cat, colors]) => (
            <div key={cat} style={legendItemStyle}>
              <span style={{ ...legendDotStyle, background: colors.border }} />
              <span style={{ textTransform: 'capitalize' }}>{cat === 'ui' ? 'Windows' : cat === 'misc' ? 'Interact' : cat}</span>
            </div>
          ))}
      </div>

      {/* Keyboard Grid */}
      <div style={{ ...keyboardGridStyle, gap: compact ? 3 : 4 }}>
        {KEY_ROWS.map((row, rIdx) => (
          <div key={rIdx} style={{ ...rowStyle, gap: compact ? 3 : 4 }}>
            {row.map((kDef) => {
              const matchedBindings = keyToBindingsMap.get(kDef.key) ?? [];
              const isBound = matchedBindings.length > 0;
              const highlightedMatch = highlightedAction
                ? matchedBindings.find((b) => b.action === highlightedAction)
                : undefined;
              const focusedBarMatch = focusedBarId
                ? matchedBindings.find((b) => ACTION_METADATA[b.action]?.barId === focusedBarId)
                : undefined;
              const primaryBinding = highlightedMatch ?? focusedBarMatch ?? matchedBindings[0];
              const boundAction = primaryBinding?.action;
              const meta = boundAction ? ACTION_METADATA[boundAction] : undefined;
              const category = meta?.category ?? primaryBinding?.category ?? 'misc';
              const isHighlighted = Boolean(boundAction && highlightedAction === boundAction);
              const colors = isBound
                ? (meta?.barId ? BAR_KEY_COLORS[meta.barId] : null) ?? CATEGORY_COLORS[category]
                : null;

              const tooltip = isBound
                ? `${kDef.label}: ${matchedBindings.map((b) => {
                  const mod = b.modifier ? `${b.modifier.toUpperCase()}+` : '';
                  return `${mod}${ACTION_METADATA[b.action]?.label ?? b.action}`;
                }).join(', ')}`
                : `${kDef.label} (unbound)`;
              const baseWidth = kDef.width ?? 32;
              const keyWidth = compact ? Math.max(26, Math.round(baseWidth * 0.84)) : baseWidth;

              return (
                <button
                  key={kDef.key}
                  type="button"
                  data-keycap={kDef.key}
                  data-bar-id={meta?.barId}
                  title={tooltip}
                  onClick={() => boundAction && onSelectAction?.(boundAction)}
                  style={{
                    ...keyCapStyle,
                    width: keyWidth,
                    height: compact ? 30 : 32,
                    ...(isBound && colors
                      ? {
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                        color: colors.text,
                      }
                      : {}),
                    ...(isHighlighted
                      ? {
                        boxShadow: `0 0 10px ${colors?.border ?? PLAYER_UI.brassBright}`,
                        transform: 'translateY(-1px)',
                        filter: 'brightness(1.3)',
                      }
                      : {}),
                  }}
                >
                  <span style={keyLabelStyle}>{kDef.label}</span>
                  {isBound && meta && (
                    <span style={keyActionSubStyle}>
                      {meta.shortSlotLabel ?? meta.label.slice(0, 5)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const boardContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '10px 14px',
  background: 'linear-gradient(180deg, rgba(246, 238, 218, 0.78), rgba(232, 218, 188, 0.78))',
  borderRadius: RADII.md,
  border: '1px solid var(--tome-rule-strong, rgba(92, 64, 28, 0.38))',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.65), 0 1px 3px rgba(70, 48, 14, 0.1)',
  marginBottom: 10,
};

const legendRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'center',
  fontSize: 10,
  fontFamily: FONTS.ui,
  color: 'var(--tome-ink-soft, #3e3021)',
};

const legendTitleStyle: React.CSSProperties = {
  color: 'var(--tome-ochre, #8f560f)',
  fontWeight: 'bold',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
};

const legendItemStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

const legendDotStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: '50%',
};

const keyboardGridStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  alignItems: 'center',
  userSelect: 'none',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  justifyContent: 'center',
  width: '100%',
};

const keyCapStyle: React.CSSProperties = {
  height: 32,
  background: 'linear-gradient(180deg, #fcf6e6 0%, #e5d5b5 100%)',
  border: '1px solid rgba(120, 88, 38, 0.42)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.75), 0 1px 2px rgba(60, 40, 10, 0.12)',
  borderRadius: 4,
  color: 'var(--tome-ink-muted, #6b5a43)',
  fontFamily: FONTS.mono,
  fontSize: 10,
  fontWeight: 'bold',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
  transition: 'all 0.15s ease',
  boxSizing: 'border-box',
};

const keyLabelStyle: React.CSSProperties = {
  lineHeight: 1,
};

const keyActionSubStyle: React.CSSProperties = {
  fontSize: 7,
  opacity: 0.85,
  marginTop: 1,
  textTransform: 'uppercase',
  lineHeight: 1,
};
